import { createAsyncThunk, createEntityAdapter, createSlice } from "@reduxjs/toolkit";
import { restClient } from "@/src/services/restClient";
const organizationsAdapter = createEntityAdapter({
  sortComparer: (a, b) => a.name.localeCompare(b.name)
});
const initialState = organizationsAdapter.getInitialState({
  status: "idle",
  error: null
});
const toMessage = (error, fallback) => {
  if (typeof error === "string" && error.trim()) return error;
  if (error instanceof Error) return error.message;
  return fallback;
};
export const fetchOrganizations = createAsyncThunk("organizations/fetchAll", async (_, {
  rejectWithValue
}) => {
  try {
    return await restClient.get("/organizations");
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to load organizations"));
  }
});
export const fetchOrganizationById = createAsyncThunk("organizations/fetchById", async (id, {
  rejectWithValue
}) => {
  try {
    return await restClient.get(`/organizations/${id}`);
  } catch (error) {
    return rejectWithValue(toMessage(error, "Organization not found"));
  }
});
export const createOrganizationThunk = createAsyncThunk("organizations/create", async (payload, {
  rejectWithValue
}) => {
  try {
    return await restClient.post("/organizations", payload);
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to create organization"));
  }
});
export const updateOrganizationThunk = createAsyncThunk("organizations/update", async ({
  id,
  patch
}, {
  rejectWithValue
}) => {
  try {
    return await restClient.patch(`/organizations/${id}`, patch);
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to update organization"));
  }
});
export const softDeleteOrganizationThunk = createAsyncThunk("organizations/softDelete", async (id, {
  rejectWithValue
}) => {
  try {
    return await restClient.patch(`/organizations/${id}`, {
      isActive: false
    });
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to deactivate organization"));
  }
});
export const reactivateOrganizationThunk = createAsyncThunk("organizations/reactivate", async (id, {
  rejectWithValue
}) => {
  try {
    return await restClient.patch(`/organizations/${id}`, {
      isActive: true
    });
  } catch (error) {
    return rejectWithValue(toMessage(error, "Failed to reactivate organization"));
  }
});
const organizationsSlice = createSlice({
  name: "organizations",
  initialState,
  reducers: {
    clearOrganizationsError(state) {
      state.error = null;
    },
    upsertListItems(state, action) {
      const mapped = action.payload.filter(org => org && (org.id || org.slug)).map(org => ({
        id: org.id || org.slug,
        name: org.orgName || org.slug || org.id,
        description: org.description || ""
      }));
      organizationsAdapter.upsertMany(state, mapped);
    }
  },
  extraReducers: builder => {
    builder.addCase(fetchOrganizations.pending, state => {
      state.status = "loading";
      state.error = null;
    }).addCase(fetchOrganizations.fulfilled, (state, action) => {
      state.status = "succeeded";
      organizationsAdapter.setAll(state, action.payload);
    }).addCase(fetchOrganizations.rejected, (state, action) => {
      state.status = "failed";
      state.error = action.payload ?? action.error.message ?? null;
    }).addCase(fetchOrganizationById.fulfilled, (state, action) => {
      organizationsAdapter.upsertOne(state, action.payload);
    }).addCase(createOrganizationThunk.fulfilled, (state, action) => {
      organizationsAdapter.upsertOne(state, action.payload);
    }).addCase(updateOrganizationThunk.fulfilled, (state, action) => {
      organizationsAdapter.upsertOne(state, action.payload);
    }).addCase(softDeleteOrganizationThunk.fulfilled, (state, action) => {
      organizationsAdapter.upsertOne(state, action.payload);
    }).addCase(reactivateOrganizationThunk.fulfilled, (state, action) => {
      organizationsAdapter.upsertOne(state, action.payload);
    });
  }
});
export const {
  clearOrganizationsError,
  upsertListItems
} = organizationsSlice.actions;
export const organizationsSelectors = organizationsAdapter.getSelectors(state => state.organizations);
export const selectOrganizationsList = organizationsSelectors.selectAll;
export const selectOrganizationsStatus = state => state.organizations.status;
export const selectOrganizationsError = state => state.organizations.error;
export default organizationsSlice.reducer;